import {CommonModule} from '@angular/common';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {Toast} from 'bootstrap';
import {fromEvent, ReplaySubject, Subject} from 'rxjs';
import {delay, take, takeUntil, tap} from 'rxjs/operators';

export enum ToastType {
    Error,
    Success,
    Warning
}

@Component({
    imports: [CommonModule],
    selector: 'truelearn-web-toast-message',
    templateUrl: './toast-message.component.html',
    styleUrls: ['./toast-message.component.scss'],
    animations: [
        trigger('srinkToast', [
            state('fullSize', style({})),
            state(
                'shrink',
                style({
                    height: 0
                })
            ),
            transition('fullSize => shrink', [animate('0.3s')])
        ])
    ]
})
export class ToastMessageComponent implements OnInit, OnDestroy {
    @ViewChild('truelearnToast', {static: true}) toastEl: ElementRef;

    @Input() message: string | TemplateRef<unknown>;
    @Input() header: string | null;
    @Input() options: Partial<Toast.Options> | null;
    @Input() type: ToastType = ToastType.Success;
    @Input() cssClass: string | null;
    @Input() set hideToast(shouldHide: boolean) {
        if (shouldHide) {
            this.hideToastStream.next();
        }
    }
    shrink = false;

    ToastType = ToastType;

    @Output()
    hidden = new EventEmitter();

    private toastInstance: Toast;
    private hideToastStream = new ReplaySubject<void>(1);
    private unsubscriber = new Subject<void>();

    constructor() {}

    ngOnInit(): void {
        this.show();
        this.hideToastStream
            .pipe(
                tap(() => this.hide()),
                takeUntil(this.unsubscriber)
            )
            .subscribe();

        const hidden = 'hidden.bs.toast';

        fromEvent(this.toastEl.nativeElement, hidden)
            .pipe(
                takeUntil(this.unsubscriber),
                take(1),
                delay(250),
                tap(() => (this.shrink = true)),
                delay(300)
            )
            .subscribe(() => this.hidden.emit());
    }

    ngOnDestroy(): void {
        this.toastInstance.dispose();
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }

    show(): void {
        let options: Partial<Toast.Options> = {};
        if (this.options) {
            options = {...this.options};
        }

        this.toastInstance = Toast.getOrCreateInstance(this.toastEl.nativeElement, options);
        this.toastInstance.show();
    }

    hide(): void {
        this.toastInstance?.hide();
    }

    isMarkup(message: string | TemplateRef<unknown>): message is TemplateRef<unknown> {
        return message instanceof TemplateRef;
    }
}
